import { supabase } from '../../../shared/lib/supabaseClient';
import { getDateRange, getTrendPeriods } from '../../../shared/utils/reportingPeriod';

const DISBURSED_STATUSES = ['Ongoing', 'Completed'];
const PAGE_SIZE = 1000;

const pad = (n) => String(n).padStart(2, '0');

// The API returns at most 1,000 rows per request, so read larger tables in pages.
// `makeQuery` builds a fresh, ordered query each time it is called.
async function fetchAllRows(makeQuery) {
  const rows = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await makeQuery().range(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    rows.push(...data);
    if (data.length < PAGE_SIZE) return rows;
  }
}

export async function getTotalOfficers() {
  const { count, error } = await supabase
    .from('officers')
    .select('*', { count: 'exact', head: true });

  if (error) throw error;
  return count ?? 0;
}

export async function getTotalStudents() {
  const { count, error } = await supabase
    .from('students')
    .select('*', { count: 'exact', head: true });

  if (error) throw error;
  return count ?? 0;
}

const paymentsQuery = (columns, range) => () => {
  let query = supabase.from('payments').select(columns).order('id');
  if (range) query = query.gte('payment_date', range.start).lte('payment_date', range.end);
  return query;
};

const disbursementsQuery = (columns, range) => () => {
  let query = supabase.from('projects').select(columns).in('status', DISBURSED_STATUSES).order('id');
  if (range) query = query.gte('expense_date', range.start).lte('expense_date', range.end);
  return query;
};

const sumAmounts = (rows) => rows.reduce((sum, row) => sum + Number(row.amount ?? 0), 0);

// `range` is { start, end } (YYYY-MM-DD, inclusive) from getDateRange(), or null for all time.
export async function getTotalPayments(range) {
  return sumAmounts(await fetchAllRows(paymentsQuery('amount', range)));
}

export async function getTotalDisbursed(range) {
  return sumAmounts(await fetchAllRows(disbursementsQuery('amount', range)));
}

// Month-by-month collected and disbursed totals for one school year, up to today.
export async function getMonthlyTrend(schoolYear) {
  const periods = getTrendPeriods(schoolYear).slice(1); // drop the month before the school year
  if (periods.length === 0) return [];

  const range = getDateRange(periods);
  const [payments, disbursements] = await Promise.all([
    fetchAllRows(paymentsQuery('amount, payment_date', range)),
    fetchAllRows(disbursementsQuery('amount, expense_date', range)),
  ]);

  const totalsByMonth = (rows, dateKey) => {
    const totals = {};
    rows.forEach((row) => {
      if (!row[dateKey]) return;
      const key = String(row[dateKey]).slice(0, 7);
      totals[key] = (totals[key] ?? 0) + Number(row.amount ?? 0);
    });
    return totals;
  };
  const collected = totalsByMonth(payments, 'payment_date');
  const disbursed = totalsByMonth(disbursements, 'expense_date');

  return periods.map(({ month, year }) => {
    const key = `${year}-${pad(month)}`;
    return { month, year, collected: collected[key] ?? 0, disbursed: disbursed[key] ?? 0 };
  });
}
